import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ClearIcon from '@mui/icons-material/Clear';

import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { Theme } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import ProductLayout from '../../../../../layout/Product';
import useNotification from '../../../../../components/_hook/useNotification';
import useLanguage from '../../../../../components/_hook/useLanguage';
import useRouter from '../../../../../components/_hook/useRouter';
import SchemaCompanyName from '../../../../../components/_schema/SchemaCompanyName';
import ButtonSubmit from '../../../../../components/_button/ButtonSubmit';
import { useDeleteDropshippingCompanyRequestsMutation } from '../../../../../../graphql/mutation/dropshippingRequestCompany/deleteDropshippingCompanyRequestsMutation';
import useRoutes from '../../../../../components/_hook/useRoutes';
import { useDropshippingRequestCompanyQuery } from '../../../../../../graphql/query/dropshippingRequestCompany/useDropshippingRequestCompanyQuery';
import Context from '../../../../../context';
import CircularProgress from '../../../../../components/_loading/CircularProgress';
import NotFound from '../../../../../components/_error/NotFound';
import DropshippedProductPriceManagementTable from '../../_module/DropshippedProductPriceManagementTable';
import Helmet from '../../../../../components/Helmet';

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  paper: {
    margin: theme.spacing(1),
    padding: theme.spacing(3),
    width: '100%',
  },
  infoContainer: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(2),
  },
  buttonRemoveDropshippingVendor: {
    backgroundColor: '#F92949',
    color: '#fff',
    '&:hover': { backgroundColor: '#e80729' },
  },
}));

function DropshipperDropshippingVendorDetail() {
  const { t } = useTranslation();
  const { notification } = useNotification();
  const { getText } = useLanguage();
  const classes = useStyles();
  const { params, history } = useRouter();
  const { companyID, toCompanyID } = params;
  const { path } = useRoutes();
  const [isRemoving, setIsRemoving] = useState(false);
  const { company: contextCompany } = useContext(Context);
  const [isShowDialogRemoving, setIsShowDialogRemoving] = useState(false);
  const [removeDialogConfirmInput, setRemoveDialogConfirmInput] = useState('');

  const { data, loading } = useDropshippingRequestCompanyQuery(
    `
		_id
		from_company_id
		to_company_id
		toCompanyData {
			_id
			origin
			verification
			slug
			name {
				la
				text
			}
		}
		status 
	`,
    {
      variables: {
        filter: {
          toCompanyId: toCompanyID,
          fromCompanyId: contextCompany._id,
        },
      },
    }
  );

  const [
    deleteDropshippingCompanyRequestsMutation,
  ] = useDeleteDropshippingCompanyRequestsMutation(`isSuccess`, {
    onCompleted: () => {
      notification.success(t('done'));
      history.push(path('product.dropshipped.vendor', { companyID }));
    },
    onError: (e) => {
      notification.error(e.message);
    },
  });

  const dropshippingRequestCompany = data
    ? data.dropshippingRequestCompanies.items[0]
    : null;
  if (loading) return <CircularProgress center color="primary" />;
  if (!dropshippingRequestCompany) {
    return <NotFound />;
  }

  function deleteDropshippingCompanyRequest() {
    if (dropshippingRequestCompany) {
      deleteDropshippingCompanyRequestsMutation({
        variables: {
          _ids: [dropshippingRequestCompany._id],
        },
      });
    }
  }

  return (
    <>
      <Grid container spacing={2}>
        <Grid container item xs={12}>
          <Paper className={classes.paper}>
            <Helmet title={`${t('dropshipping_vendor')} | ${t('product')}`} />
            {dropshippingRequestCompany.toCompanyData && (
              <Dialog
                open={isShowDialogRemoving}
                onClose={() => setIsShowDialogRemoving(false)}
                maxWidth="sm"
                fullWidth
              >
                <DialogTitle>
                  {t(
                    'are_you_sure_you_want_to_remove_this_dropshipping_vendor'
                  )}
                </DialogTitle>
                <DialogContent>
                  <Grid
                    container
                    direction="row"
                    spacing={2}
                    justifyContent="center"
                  >
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" align="center">
                        {t(
                          'remove_dropshipping_vendor_will_also_remove_all_related_dropshipped_product_and_dropshipping_request'
                        )}
                      </Typography>
                    </Grid>
                    <Grid
                      container
                      item
                      xs={12}
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Typography variant="subtitle1" display="inline">
                        {t('enter')}
                      </Typography>
                      <Typography variant="h6" display="inline" color="primary">
                        &nbsp;
                        <SchemaCompanyName
                          company={dropshippingRequestCompany.toCompanyData}
                        />
                        &nbsp;
                      </Typography>
                      <Typography variant="subtitle1" display="inline">
                        {t('to_continue_this_action')}
                      </Typography>
                    </Grid>
                    <Grid container item xs={8}>
                      <TextField
                        required
                        fullWidth
                        placeholder={t('enter')}
                        value={removeDialogConfirmInput}
                        onChange={(e) =>
                          setRemoveDialogConfirmInput(e.target.value)
                        }
                      />
                    </Grid>
                  </Grid>
                </DialogContent>
                <DialogActions>
                  <Grid
                    container
                    item
                    xs={12}
                    spacing={1}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Grid container item xs={6}>
                      <Button
                        variant="contained"
                        size="large"
                        fullWidth
                        color="primary"
                        disabled={
                          getText(
                            dropshippingRequestCompany.toCompanyData.name
                          ) !== removeDialogConfirmInput
                        }
                        onClick={deleteDropshippingCompanyRequest}
                      >
                        {t('confirm')}
                      </Button>
                    </Grid>
                    <Grid container item xs={12} justifyContent="center">
                      <Button onClick={() => setIsShowDialogRemoving(false)}>
                        {t('cancel')}
                      </Button>
                    </Grid>
                  </Grid>
                </DialogActions>
              </Dialog>
            )}
            <Grid container spacing={2} item xs={12}>
              <Grid container item className={classes.infoContainer}>
                <Grid item xs={12}>
                  <Typography variant="h5" align="center">
                    {t('dropshipping_vendor')}:&nbsp;
                    <b>
                      {dropshippingRequestCompany.toCompanyData && (
                        <SchemaCompanyName
                          company={dropshippingRequestCompany.toCompanyData}
                        />
                      )}
                    </b>
                  </Typography>
                </Grid>
              </Grid>
              <Grid container item spacing={1} justifyContent="center">
                <ButtonSubmit
                  className={classes.buttonRemoveDropshippingVendor}
                  iconPosition="left"
                  variant="contained"
                  color="secondary"
                  onClick={() => setIsShowDialogRemoving(true)}
                  isSubmitting={isRemoving}
                  label={t('remove_dropshipping_vendor')}
                  icon={<ClearIcon />}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid container item xs={12}>
          <DropshippedProductPriceManagementTable
            dropshippedCompanyId={dropshippingRequestCompany.to_company_id}
          />
        </Grid>
      </Grid>
    </>
  );
}

export default () => (
  <ProductLayout main={<DropshipperDropshippingVendorDetail />} />
);
